import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { FilePdfIcon as FilePdf, DogIcon as Dog, SparkleIcon as Sparkle, ReceiptIcon as Receipt } from "@phosphor-icons/react";
import { formatDate, thumbUrl } from "@/lib/utils-app";
import PdfModal from "@/components/PdfModal";

export default function MyCertificates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/certificates/me")
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const token = localStorage.getItem("token") || "";
  const certUrl = (dogId) =>
    `${API}/dogs/${dogId}/certificate.pdf?auth=${encodeURIComponent(token)}`;

  return (
    <AppShell>
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
          Library
        </p>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">
          My pedigree certificates
        </h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          Every pedigree certificate you've unlocked — re-download any of them here.
        </p>
      </div>

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="certificates-grid">
          {rows.map((r) => (
            <CertificateCard key={r.id} row={r} href={certUrl(r.dog.id)} />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function CertificateCard({ row, href }) {
  const d = row.dog;
  // Local modal state — one per card so multiple cards can be opened
  // independently without leaking state across them.
  const [showPdf, setShowPdf] = useState(false);
  const reasonLabel = row.reason
    ? { admin: "Admin", pro: "Pro plan", purchased: "Purchased" }[row.reason] || "Unlocked"
    : row.free
    ? "Unlocked"
    : "Paid";
  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-[2px] transition-transform flex flex-col"
      data-testid={`certificate-card-${d.id}`}
    >
      <div className="aspect-[4/3] bg-[#FAFAFA] relative">
        {d.photos?.[0] ? (
          <img src={thumbUrl(d.photos[0])} alt={d.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Dog size={40} weight="duotone" className="text-[#6B6B6B]/40" />
          </div>
        )}
        <span
          className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-[0.15em] rounded-full px-2.5 py-1 border ${
            row.free
              ? "bg-[#0E0E0E] text-white border-[#0E0E0E]"
              : "bg-[#C1272D] text-white border-[#C1272D]"
          }`}
        >
          {row.free ? (
            <span className="flex items-center gap-1">
              <Sparkle size={10} weight="fill" /> {reasonLabel}
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <Receipt size={10} weight="fill" /> Paid
            </span>
          )}
        </span>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <Link
          to={`/dogs/${d.id}`}
          className="font-spectral text-xl tracking-tight hover:text-crimson transition-colors"
        >
          {d.name}
        </Link>
        <p className="text-xs text-[#6B6B6B] mt-1">{d.breed}</p>
        {d.registration_number && (
          <p className="text-[10px] font-mono text-[#6B6B6B] mt-1 truncate">
            Reg #{d.registration_number}
          </p>
        )}
        <p className="text-[10px] text-[#6B6B6B] mt-3">
          Unlocked {formatDate(row.created_at)}
        </p>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setShowPdf(true)}
          className="mt-4 w-full"
          data-testid={`download-cert-${d.id}`}
        >
          <Button asChild={false} className="w-full bg-ink hover:bg-crimson text-white rounded-full pointer-events-none">
            <span className="inline-flex items-center justify-center">
              <FilePdf size={14} className="mr-2" weight="duotone" /> View pedigree PDF
            </span>
          </Button>
        </button>
        <PdfModal
          open={showPdf}
          onClose={() => setShowPdf(false)}
          src={href}
          title={`Pedigree certificate · ${d.name || "Dog"}`}
          downloadName={`pedigree-${(d.name || d.id).replace(/\s+/g, "-").toLowerCase()}.pdf`}
        />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-16 text-center"
      data-testid="certificates-empty"
    >
      <FilePdf size={48} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-4" />
      <h2 className="font-spectral text-2xl tracking-tight">
        No pedigree certificates yet
      </h2>
      <p className="text-[#6B6B6B] mt-2 max-w-md mx-auto">
        Unlock a printable pedigree certificate from any dog's page — it lands here so you can
        re-download it anytime.
      </p>
      <Link to="/dogs" className="inline-block mt-6">
        <Button variant="outline" className="rounded-full border-[#E5E5E5]">
          Browse dogs
        </Button>
      </Link>
    </div>
  );
}
